/* ===========================================================
   editor/nodes/sandbox.js — live HTML / CSS / JS preview

   A block with three code parts (HTML, CSS, JS) that are ordinary
   editable text, so live co-editing and undo work in them like
   anywhere else. Under them, the result renders as you type, with
   console output below.

   SECURITY: the preview runs in an <iframe sandbox="allow-scripts">
   (no allow-same-origin) loading /sandbox/preview.html, which is also
   served with `Content-Security-Policy: sandbox allow-scripts`. So
   the code gets an opaque "null" origin: no cookies, no storage, no
   way to call our API (which wants a custom header and our Origin),
   no popups, no top-level navigation, no forms.

   Someone else's doc doesn't run on its own: you press "Run preview"
   first (env.autoRunPreviews decides).

   Markdown form:
     :::sandbox
     ```html  …  ```
     ```css   …  ```
     ```js    …  ```
     :::
   =========================================================== */

import { Node } from '@tiptap/core';
import { env } from '../env.js';
import { h, svgIcon } from '../../lib/dom.js';
import { ICONS } from '../../lib/icons.js';

const PARTS = ['html', 'css', 'js'];
const LABEL = { html: 'HTML', css: 'CSS', js: 'JavaScript' };

export const SandboxCode = Node.create({
  name: 'sandboxCode',
  content: 'text*',
  marks: '',
  code: true,
  defining: true,
  addAttributes: () => ({
    lang: { default: 'html', parseHTML: el => el.getAttribute('data-sandbox-code') || 'html', renderHTML: a => ({ 'data-sandbox-code': a.lang }) },
  }),
  parseHTML: () => [{ tag: 'pre[data-sandbox-code]', preserveWhitespace: 'full' }],
  renderHTML: ({ HTMLAttributes }) => ['pre', HTMLAttributes, ['code', 0]],
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.state.selection.$from.parent.type.name !== this.name) return false;
        return this.editor.commands.insertContent('  ');
      },
    };
  },
});

export const Sandbox = Node.create({
  name: 'sandbox',
  group: 'block',
  content: 'sandboxCode{3}',
  isolating: true,
  defining: true,
  addAttributes: () => ({
    height: { default: 260, parseHTML: el => Number(el.getAttribute('data-height')) || 260, renderHTML: a => ({ 'data-height': a.height }) },
  }),
  parseHTML: () => [{ tag: 'div[data-sandbox]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', { 'data-sandbox': '', ...HTMLAttributes }, 0],
  addNodeView() { return props => sandboxView(props); },
  addCommands() {
    return {
      insertSandbox: () => ({ commands }) => commands.insertContent(newSandbox()),
    };
  },
  markdownTokenName: 'sandbox',
  markdownTokenizer: {
    name: 'sandbox',
    level: 'block',
    start: src => src.indexOf(':::sandbox'),
    tokenize(src) {
      const m = /^:::sandbox[^\n]*\n([\s\S]*?)\n:::[ \t]*(?:\n|$)/.exec(src);
      if (!m) return undefined;
      return { type: 'sandbox', raw: m[0], text: m[1] };
    },
  },
  parseMarkdown: (token, helpers) => {
    const code = { html: '', css: '', js: '' };
    for (const f of token.text.matchAll(/```(html|css|js|javascript)[^\n]*\n([\s\S]*?)\n?```/g)) {
      code[f[1] === 'javascript' ? 'js' : f[1]] = f[2];
    }
    return helpers.createNode('sandbox', {}, PARTS.map(p => helpers.createNode('sandboxCode', { lang: p }, code[p] ? [helpers.createTextNode(code[p])] : [])));
  },
  renderMarkdown: node => {
    const parts = (node.content || []).map(c => {
      const text = (c.content || []).map(t => t.text || '').join('');
      return `\`\`\`${c.attrs?.lang || 'html'}\n${text}\n\`\`\``;
    });
    return `:::sandbox\n${parts.join('\n')}\n:::`;
  },
});

export function newSandbox(code = {}) {
  const starter = {
    html: '<h1>Hello!</h1>\n<button id="b">Click me</button>',
    css: 'body { font-family: system-ui, sans-serif; padding: 16px; }\nh1 { color: #2f7a52; }',
    js: "document.getElementById('b').onclick = () => {\n  console.log('Clicked at', new Date().toLocaleTimeString());\n};",
  };
  return {
    type: 'sandbox',
    content: PARTS.map(lang => ({
      type: 'sandboxCode', attrs: { lang },
      content: (code[lang] ?? starter[lang]) ? [{ type: 'text', text: code[lang] ?? starter[lang] }] : [],
    })),
  };
}

let sandboxCount = 0;

function sandboxView({ node, editor }) {
  let current = node;
  let tab = 'html';
  let live = env.autoRunPreviews();
  let frame = null;
  let renderTimer = null;
  let lastBeat = 0;
  let watchdog = null;
  let runId = 0;

  const dom = h('div.sandbox', { 'data-tab': tab });
  const tabs = PARTS.map(p => h('button.sb-tab', {
    type: 'button', role: 'tab', 'aria-selected': String(p === tab),
    onclick: () => { tab = p; dom.dataset.tab = p; tabs.forEach((t, i) => t.setAttribute('aria-selected', String(PARTS[i] === p))); },
  }, LABEL[p]));
  const runBtn = h('button.cb-btn.run', { type: 'button', onclick: () => { live = true; paintRun(); render(true); } }, svgIcon(ICONS.play, 15), h('span', {}, 'Run preview'));
  // the side pane (DocPage PreviewPane): the doc moves left, the preview sits beside it, full screen if you like
  const key = `sb${++sandboxCount}`;
  const paneBtn = h('button.cb-btn', { type: 'button', title: 'Show the preview beside the doc', onclick: () => {
    live = true; paintRun();
    window.dispatchEvent(new CustomEvent('dp:preview-open', { detail: { key, code: texts() } }));
  } }, svgIcon(ICONS.open, 15), h('span', {}, 'Side pane'));
  const head = h('div.sb-head', { contentEditable: 'false' },
    h('span.sb-title', {}, svgIcon(ICONS.sandbox, 15), 'Live preview'),
    h('div.sb-tabs', { role: 'tablist', 'aria-label': 'Code part' }, ...tabs),
    h('span.cb-space'), runBtn, paneBtn);
  const code = h('div.sb-code');
  const stage = h('div.sb-stage', { contentEditable: 'false' });
  const consoleEl = h('div.sb-console', { contentEditable: 'false', role: 'log', 'aria-live': 'polite', 'aria-label': 'Console output' });
  const consoleWrap = h('details.sb-console-wrap', { contentEditable: 'false' }, h('summary', {}, 'Console'), consoleEl);
  const placeholder = h('div.sb-paused', {}, h('p', {}, 'This preview runs code written by someone else.'), h('p', {}, 'Press "Run preview" to see it. It runs walled off from your account.'));
  dom.append(head, code, stage, consoleWrap);

  function paintRun() { runBtn.hidden = live; }
  paintRun();

  function texts() {
    const out = {};
    current.forEach((child, _, i) => { out[child.attrs.lang || PARTS[i]] = child.textContent; });
    return out;
  }

  function log(level, text) {
    consoleWrap.open = true;
    const line = h(`div.sb-line.l-${level}`, {}, text);
    consoleEl.append(line);
    while (consoleEl.childElementCount > 200) consoleEl.firstElementChild.remove();
    consoleEl.scrollTop = consoleEl.scrollHeight;
  }

  const onMessage = e => {
    if (!frame || e.source !== frame.contentWindow) return;
    const m = e.data || {};
    if (m.run !== runId) return;
    if (m.type === 'ready') frame.contentWindow.postMessage({ type: 'render', run: runId, ...texts() }, '*');
    else if (m.type === 'alive') lastBeat = Date.now();
    else if (m.type === 'console') log(m.level, String(m.text).slice(0, 4000));
  };
  window.addEventListener('message', onMessage);

  function render(now = false) {
    clearTimeout(renderTimer);
    if (!live) { stage.replaceChildren(placeholder); return; }
    renderTimer = setTimeout(() => {
      runId++;
      consoleEl.replaceChildren();
      frame?.remove();
      frame = h('iframe.sb-frame', {
        sandbox: 'allow-scripts',
        src: `/sandbox/preview#${runId}`,
        title: 'Live preview',
        referrerpolicy: 'no-referrer',
        style: { height: `${current.attrs.height || 260}px` },
      });
      frame.setAttribute('sandbox', 'allow-scripts');
      stage.replaceChildren(frame);
      lastBeat = Date.now();
      clearInterval(watchdog);
      watchdog = setInterval(() => {
        if (Date.now() - lastBeat > 5000 && frame) {
          frame.remove();
          frame = null;
          clearInterval(watchdog);
          stage.replaceChildren(h('div.sb-paused', {}, h('p', {}, 'This preview got stuck (an endless loop, maybe?), so I stopped it.'),
            h('button.cb-btn', { type: 'button', onclick: () => render(true) }, 'Try again')));
        }
      }, 1500);
    }, now ? 0 : 450);
  }
  render(true);

  return {
    dom,
    contentDOM: code,
    update(n) {
      if (n.type !== current.type) return false;
      const before = JSON.stringify(texts());
      current = n;
      if (JSON.stringify(texts()) !== before) {
        render();
        window.dispatchEvent(new CustomEvent('dp:preview-code', { detail: { key, code: texts() } }));
      }
      return true;
    },
    stopEvent: e => head.contains(e.target) || stage.contains(e.target) || consoleWrap.contains(e.target),
    ignoreMutation: m => m.type !== 'selection' && !code.contains(m.target),
    destroy() {
      clearTimeout(renderTimer);
      clearInterval(watchdog);
      window.removeEventListener('message', onMessage);
      frame?.remove();
    },
  };
}
